import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Announcement as AnnouncementIcon,
  MoreVert as MoreVertIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { Chat, ChatType } from '../../utils/types';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';

interface ChatListProps {
  chats: Chat[];
  selectedChat: Chat | null;
  onChatSelect: (chat: Chat) => void;
  currentUserId: string;
}

const ChatList: React.FC<ChatListProps> = ({
  chats,
  selectedChat,
  onChatSelect,
  currentUserId
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChatForMenu, setSelectedChatForMenu] = useState<Chat | null>(null);

  // تصفية المحادثات بناءً على البحث
  const filteredChats = chats.filter(chat =>
    chat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.participants.some(p => p.userName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // الحصول على أيقونة نوع المحادثة
  const getChatTypeIcon = (type: ChatType) => {
    switch (type) {
      case 'private':
        return <PersonIcon />;
      case 'group':
        return <GroupIcon />;
      case 'announcement':
        return <AnnouncementIcon />;
      default:
        return <GroupIcon />;
    }
  };

  // الحصول على لون نوع المحادثة
  const getChatTypeColor = (type: ChatType) => {
    switch (type) {
      case 'private':
        return theme.palette.primary.main;
      case 'group':
        return theme.palette.secondary.main;
      case 'announcement':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // الحصول على اسم المحادثة المناسب
  const getChatDisplayName = (chat: Chat) => {
    if (chat.type === 'private') {
      // في المحادثات الخاصة، عرض اسم المستخدم الآخر
      const otherParticipant = chat.participants.find(p => p.userId !== currentUserId);
      return otherParticipant?.userName || 'مستخدم غير معروف';
    }
    return chat.name;
  };

  // الحصول على عدد الرسائل غير المقروءة
  const getUnreadCount = (chat: Chat) => {
    if (!chat.lastMessage) return 0;
    
    // هذا مجرد مثال - يجب تنفيذ منطق حقيقي لحساب الرسائل غير المقروءة
    const isRead = chat.lastMessage.readBy.includes(currentUserId);
    return isRead ? 0 : 1;
  };

  // تنسيق وقت آخر رسالة
  const formatLastMessageTime = (date: Date) => {
    try {
      return formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: ar 
      });
    } catch (error) {
      return 'الآن';
    }
  };

  // فتح قائمة الخيارات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chat: Chat) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedChatForMenu(chat);
  };

  // إغلاق قائمة الخيارات
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChatForMenu(null);
  };

  // كتم الإشعارات
  const handleMuteChat = () => {
    // تنفيذ منطق كتم الإشعارات
    console.log('Mute chat:', selectedChatForMenu?.id);
    handleMenuClose();
  };

  // حذف المحادثة
  const handleDeleteChat = () => {
    // تنفيذ منطق حذف المحادثة
    console.log('Delete chat:', selectedChatForMenu?.id);
    handleMenuClose();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* شريط البحث */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="البحث في المحادثات..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </Box>

      {/* قائمة المحادثات */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredChats.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد محادثات'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredChats.map((chat, index) => {
              const isSelected = selectedChat?.id === chat.id;
              const unreadCount = getUnreadCount(chat);
              const displayName = getChatDisplayName(chat);

              return (
                <React.Fragment key={chat.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => onChatSelect(chat)}
                      sx={{
                        py: 2,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          borderRight: `3px solid ${theme.palette.primary.main}`,
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={unreadCount}
                          color="error"
                          overlap="circular"
                        >
                          <Avatar
                            sx={{
                              bgcolor: getChatTypeColor(chat.type),
                              width: 48,
                              height: 48
                            }}
                          >
                            {getChatTypeIcon(chat.type)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={unreadCount > 0 ? 'bold' : 'medium'}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}
                            >
                              {displayName}
                            </Typography>
                            
                            {chat.type !== 'private' && (
                              <Chip
                                size="small"
                                label={chat.participants.length}
                                sx={{
                                  height: 20,
                                  fontSize: '0.75rem',
                                  bgcolor: alpha(getChatTypeColor(chat.type), 0.1),
                                  color: getChatTypeColor(chat.type)
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {chat.lastMessage && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontWeight: unreadCount > 0 ? 'medium' : 'normal'
                                }}
                              >
                                {chat.lastMessage.senderName}: {chat.lastMessage.content}
                              </Typography>
                            )}
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {chat.lastMessage 
                                  ? formatLastMessageTime(chat.lastMessage.timestamp)
                                  : formatLastMessageTime(chat.createdAt)
                                }
                              </Typography>
                              
                              {/* مؤشر الحالة */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {chat.settings.muteNotifications && (
                                  <CircleIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        }
                      />

                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, chat)}
                        sx={{ ml: 1 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                  
                  {index < filteredChats.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* قائمة الخيارات */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleMuteChat}>
          <Typography variant="body2">
            {selectedChatForMenu?.settings.muteNotifications ? 'إلغاء كتم الإشعارات' : 'كتم الإشعارات'}
          </Typography>
        </MenuItem>
        
        {selectedChatForMenu?.type !== 'announcement' && (
          <MenuItem onClick={handleDeleteChat} sx={{ color: 'error.main' }}>
            <Typography variant="body2">حذف المحادثة</Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ChatList;
